import React, { useEffect, useState } from "react";
import styles from "./facility.module.css";
import { 
  CheckCircle, 
  MapPin, 
  Quote, 
  ChevronUp, 
  Shield, 
  Leaf, 
  Settings, 
  Users, 
  Clock,
  DollarSign,
  AlertTriangle
} from "lucide-react";
import { motion, useAnimation } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { animateScroll as scroll } from "react-scroll";

const offerings = [
  {
    title: "Technical Maintenance",
    icon: <Settings size={24} />,
    points: [
      "Preventive & emergency electrical, plumbing, HVAC services",
      "Elevator, fire safety, and generator upkeep",
      "Energy audits and AMC (Annual Maintenance Contracts)",
    ],
  },
  {
    title: "Housekeeping & Cleaning",
    icon: <Leaf size={24} />,
    points: [
      "Common area deep cleaning",
      "Landscape and garden maintenance",
      "Garbage collection & waste segregation",
    ],
  },
  {
    title: "Security & Surveillance",
    icon: <Shield size={24} />,
    points: [
      "24x7 manned security services",
      "CCTV installation and monitoring",
      "Visitor and staff entry control with digital logbooks",
    ],
  },
  {
    title: "Administrative Support",
    icon: <Users size={24} />,
    points: [
      "Vendor management and inventory control",
      "Resident grievance redressal",
      "Helpdesk operations and monthly reporting",
    ],
  },
  {
    title: "Utilities & Compliance",
    icon: <Clock size={24} />,
    points: [
      "Water management and tank cleaning",
      "STP/WTP operations",
      "Government regulation compliance (Fire NOC, RERA, GST etc.)",
    ],
  },
];

const highlights = [
  {
    icon: <Users size={24} />,
    title: "Trained On-Ground Teams",
    description: "Professionally trained staff with uniforms, ID cards, and soft-skill training to ensure discipline and efficiency."
  },
  {
    icon: <Clock size={24} />,
    title: "24x7 Support & Tech Dashboard",
    description: "Real-time updates, alerts, and digital logs via our Facility Tech Dashboard."
  },
  {
    icon: <DollarSign size={24} />,
    title: "Cost Optimization",
    description: "Transparent pricing, resource optimization, and long-term asset protection that saves costs."
  },
  {
    icon: <AlertTriangle size={24} />,
    title: "Emergency Ready",
    description: "Quick-response teams available for repairs, flooding, electrical faults, or health emergencies."
  },
  {
    icon: <Settings size={24} />,
    title: "Custom Packages",
    description: "Pay only for what you need — choose from monthly, quarterly, or yearly plans."
  }
];

const propertyTypes = [
  {
    title: "Residential Societies",
    description: "Apartments, gated villas, and housing complexes",
    icon: "🏠"
  },
  {
    title: "Commercial Parks",
    description: "Office buildings and business parks",
    icon: "🏢"
  },
  {
    title: "Retail Complexes",
    description: "Shopping malls and mixed-use developments",
    icon: "🛍️"
  },
  {
    title: "Holiday Homes",
    description: "Vacation properties & rental investments",
    icon: "🌴"
  },
  {
    title: "Luxury Communities",
    description: "High-rise towers and premium residences",
    icon: "✨"
  }
];

const locations = ["Mumbai", "Pune", "Bangalore", "Goa", "Hyderabad", "Delhi NCR"];

const testimonials = [
  {
    name: "RWA President, Magarpatta Greens",
    role: "Residential Society",
    text: "We handed over our 120-unit residential society in Pune to Property Plateau. Their team maintains everything—from cleaning to security—with professionalism.",
  },
  {
    name: "Anurag J.",
    role: "Gated Community, Bangalore",
    text: "Their facility management tech dashboard gives us real-time tracking of complaints and expenses. Extremely useful for gated communities.",
  },
];

const stats = [
  { value: 150, label: "Properties Managed" },
  { value: 95, label: "Client Retention Rate" },
  { value: 24, label: "Average Response Time (mins)" },
  { value: 200, label: "Trained Professionals" },
];

const FeatureCard = ({ title, icon, points }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);

  return (
    <motion.div
      ref={ref}
      className={styles.card}
      initial="hidden"
      animate={controls}
      variants={{
        visible: { opacity: 1, y: 0 },
        hidden: { opacity: 0, y: 30 },
      }}
      transition={{ duration: 0.5 }}
      whileHover={{ scale: 1.03, boxShadow: "0 10px 25px rgba(0,0,0,0.1)" }}
    >
      <div className={styles.cardIcon}>{icon}</div>
      <h3>{title}</h3>
      <ul>
        {points.map((point, i) => (
          <li key={i}>
            <CheckCircle size={18} color="#4CAF50" /> {point}
          </li>
        ))}
      </ul>
    </motion.div>
  );
};

const Counter = ({ value, label }) => {
  return (
    <motion.div
      className={styles.counterBox}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
    >
      <div className={styles.counterNumber}>
        {value}+
      </div>
      <div className={styles.counterLabel}>{label}</div>
    </motion.div>
  );
};

const HighlightItem = ({ icon, title, description }) => {
  return (
    <motion.div
      className={styles.highlightItem}
      whileHover={{ y: -5 }}
      transition={{ duration: 0.3 }}
    >
      <div className={styles.highlightIcon}>{icon}</div>
      <h3>{title}</h3>
      <p>{description}</p>
    </motion.div>
  );
};

const PropertyTypeCard = ({ title, description, icon }) => {
  return (
    <motion.div
      className={styles.propertyTypeCard}
      whileHover={{ y: -5 }}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
    >
      <div className={styles.propertyTypeIcon}>{icon}</div>
      <h3>{title}</h3>
      <p>{description}</p>
    </motion.div>
  );
};

const TestimonialCard = ({ text, name, role }) => {
  return (
    <motion.div
      className={styles.testimonialCard}
      whileHover={{ y: -5 }}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
    >
      <Quote size={24} className={styles.quoteIcon} />
      <p>"{text}"</p>
      <div className={styles.testimonialAuthor}>
        <strong>{name}</strong>
        <span>{role}</span>
      </div>
    </motion.div>
  );
};

export default function FacilityManagementPage() {
  const [loading, setLoading] = useState(true);
  const [showTop, setShowTop] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 1200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setShowTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => scroll.scrollToTop({ smooth: true });

  if (loading) {
    return (
      <div className={styles.loaderContainer}>
        <div className={styles.loader}></div>
        <p>Loading Facility Management Services...</p>
      </div>
    );
  }

  return (
    <motion.div
      className={styles.facilityPage}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero Section */}
      <motion.section
        className={styles.heroSection}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className={styles.heroBlob}></div>
        <div className={styles.heroContent}>
          <motion.h1
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Comprehensive Facility Management Solutions
          </motion.h1>
          <motion.p
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            Property Plateau delivers end-to-end facility management services that enhance property performance, ensure tenant satisfaction, and maintain asset value.
          </motion.p>
          <motion.div
            className={styles.heroStats}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {stats.map((stat, index) => (
              <Counter key={index} value={stat.value} label={stat.label} />
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Intro Section */}
      <section className={styles.introSection}>
        <motion.div
          className={styles.introContent}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2>Your Complete Property Care Partner</h2>
          <p>
            Whether it's a housing society, commercial complex, or gated villa community, we provide tech-enabled, end-to-end facility care with reliability and professionalism. Our integrated approach combines skilled manpower with smart technology for seamless operations.
          </p>
        </motion.div>
      </section>

      {/* Offerings Section */}
      <motion.section 
        className={styles.offerings}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className={styles.sectionHeader}>
          <h2>Our Comprehensive Services</h2>
          <p>Tailored solutions for every facility management need</p>
        </div>
        <div className={styles.offeringsGrid}>
          {offerings.map((item, idx) => (
            <FeatureCard
              key={idx}
              title={item.title}
              icon={item.icon}
              points={item.points}
            />
          ))}
        </div>
      </motion.section>

      {/* Property Types Section */}
      <motion.section 
        className={styles.propertyTypes}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className={styles.sectionHeader}>
          <h2>Trusted by Builders, RWA, & Corporates</h2>
          <p>We manage properties for:</p>
        </div>
        <div className={styles.propertyTypesGrid}>
          {propertyTypes.map((type, idx) => (
            <PropertyTypeCard
              key={idx}
              title={type.title}
              description={type.description}
              icon={type.icon}
            />
          ))}
        </div>
      </motion.section>

      {/* Highlights Section */}
      <motion.section 
        className={styles.highlights}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className={styles.sectionHeader}>
          <h2>Why Property Plateau Facility Management?</h2>
          <p>Our commitment to excellence in every service we provide</p>
        </div>
        <div className={styles.highlightsGrid}>
          {highlights.map((item, idx) => (
            <HighlightItem 
              key={idx}
              icon={item.icon}
              title={item.title}
              description={item.description}
            />
          ))}
        </div>
      </motion.section>

      {/* Locations Section */}
      <motion.section 
        className={styles.locations}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className={styles.sectionHeader}>
          <h2>Our Service Network</h2>
          <p>Currently serving major cities across India</p>
        </div>
        <div className={styles.locationList}>
          {locations.map((loc, idx) => (
            <motion.div
              key={idx}
              className={styles.locationItem}
              whileHover={{ scale: 1.1 }}
              transition={{ duration: 0.3 }}
            >
              <MapPin size={18} color="#2196F3" />
              {loc}
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* Testimonials Section */}
      <motion.section 
        className={styles.testimonials}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className={styles.sectionHeader}>
          <h2>Client Experiences</h2>
          <p>What our partners say about our services</p>
        </div>
        <div className={styles.testimonialGrid}>
          {testimonials.map((item, idx) => (
            <TestimonialCard
              key={idx}
              text={item.text}
              name={item.name}
              role={item.role}
            />
          ))}
        </div>
      </motion.section>

      {/* CTA Section */}
      <motion.section 
        className={styles.ctaSection}
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className={styles.ctaContent}>
          <h2>Ready to Elevate Your Property Management?</h2>
          <p>Let our experts maintain your property with the highest standards of care and professionalism.</p>
          <div className={styles.ctaButtons}>
         
         
                <motion.a
            href="/contactus"
            className={styles.secondaryButton}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Speak with Our Facility Expert
          </motion.a>
         
          </div>
        </div>
      </motion.section>

      {/* Scroll To Top Button */}
      {showTop && (
        <motion.button
          className={styles.scrollTop}
          onClick={scrollToTop}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          whileHover={{ scale: 1.1 }}
        >
          <ChevronUp size={24} />
        </motion.button>
      )}
    </motion.div>
  );
}